const SVG_NS = 'http://www.w3.org/2000/svg';
const SELECTOR = '.animated-button';

const EASE_SMOOTH = 'cubic-bezier(0.65, 0, 0.35, 1)';
const EASE_SMOOTH_IN = 'cubic-bezier(0.4, 0, 1, 1)';
const EASE_SMOOTH_OUT = 'cubic-bezier(0, 0, 0.2, 1)';

const DURATION_TEXT_OUT = 0.42;
const DURATION_TEXT_IN = 0.52;
const DURATION_FILL = 0.45;

function createSvgEl(tag, attrs = {}) {
  const el = document.createElementNS(SVG_NS, tag);
  Object.entries(attrs).forEach(([key, value]) => el.setAttribute(key, value));
  return el;
}

function setTransform(el, transform) {
  if (!el) return;
  el.style.willChange = 'transform';
  el.style.backfaceVisibility = 'hidden';
  el.style.transform = transform;
}

function setFillTransform(el, yPercent) {
  if (!el) return;
  el.style.transformBox = 'fill-box';
  el.style.transformOrigin = 'center top';
  el.style.willChange = 'transform';
  el.style.transform = `translate3d(0, ${yPercent}%, 0)`;
}

function toElements(target) {
  if (!target) return [];
  return target instanceof NodeList || Array.isArray(target) ? [...target] : [target];
}

function createTimeline(targets) {
  let reversed = true;
  let pendingFrame = 0;

  const apply = (varsList, prop) => {
    varsList.forEach(({ el, fromVars, toVars, duration, ease }) => {
      toElements(el).forEach((node) => {
        if (!node) return;
        const value = prop === 'from' ? fromVars : toVars;
        const time = duration ?? DURATION_TEXT_OUT;
        const easing = ease || EASE_SMOOTH;
        node.style.transition = `transform ${time}s ${easing}`;
        if (value?.transform !== undefined) node.style.transform = value.transform;
      });
    });
  };

  targets.forEach((target) => {
    toElements(target.el).forEach((node) => {
      if (!node || !target.fromVars) return;
      node.style.transition = 'none';
      if (target.fromVars.transform !== undefined) node.style.transform = target.fromVars.transform;
    });
  });

  return {
    play() {
      if (!reversed) return;
      reversed = false;
      if (pendingFrame) cancelAnimationFrame(pendingFrame);
      pendingFrame = requestAnimationFrame(() => {
        pendingFrame = requestAnimationFrame(() => {
          pendingFrame = 0;
          apply(targets, 'to');
        });
      });
    },
    reverse() {
      if (reversed) return;
      reversed = true;
      if (pendingFrame) cancelAnimationFrame(pendingFrame);
      pendingFrame = requestAnimationFrame(() => {
        pendingFrame = requestAnimationFrame(() => {
          pendingFrame = 0;
          apply(targets, 'from');
        });
      });
    },
    kill() {
      if (pendingFrame) cancelAnimationFrame(pendingFrame);
      pendingFrame = 0;
    },
  };
}

class AnimatedButton {
  static get selector() {
    return SELECTOR;
  }

  constructor(el) {
    this.dom = {
      el,
      btnInner: el.querySelector('[data-button-inner]'),
      btnContent: el.querySelector('[data-button-content]'),
      btnContentCloned: el.querySelector('[data-button-content-cloned]'),
      btnIcon: el.querySelector('[data-button-icon-original]'),
    };

    this.svgSettings = {
      width: '100%',
      height: '100%',
      rx: '0',
      ry: '0',
    };

    this._reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.build();
    this.addEvents();
  }

  build() {
    if (this._reducedMotion) return;

    this.buildSvgCanvas();
    this.buildBtnBg();
    this.buildBtnFill();
    this.prepareContentLayout();
    this.setInitialStates();
    this.buildAnimateContentTl();
  }

  addEvents() {
    if (this._reducedMotion) return;

    this._onMouseEnter = () => this.mouseEvents({ type: 'mouseenter' });
    this._onMouseLeave = () => this.mouseEvents({ type: 'mouseleave' });

    this.dom.el.addEventListener('mouseenter', this._onMouseEnter);
    this.dom.el.addEventListener('mouseleave', this._onMouseLeave);
  }

  buildSvgCanvas() {
    this.canvas = createSvgEl('svg', {
      class: 'btn-svg',
      width: '100%',
      height: '100%',
    });

    this.dom.el.appendChild(this.canvas);
  }

  createRect(attrs) {
    const rect = createSvgEl('rect', {
      width: this.svgSettings.width,
      height: this.svgSettings.height,
      x: '0',
      y: '0',
      ...attrs,
    });

    if (this.svgSettings.rx) rect.setAttribute('rx', this.svgSettings.rx);
    if (this.svgSettings.ry) rect.setAttribute('ry', this.svgSettings.ry);

    return rect;
  }

  getRectColors(type) {
    // Colors come from theme.css (.btn-bg / .btn-fill) — avoid getComputedStyle.
    if (type === 'bg') return { fill: 'none', stroke: 'none' };
    if (type === 'fill') return { fill: 'currentColor', stroke: 'currentColor' };
    return {};
  }

  buildBtnBg() {
    this.btnBg = this.createRect({
      ...this.getRectColors('bg'),
      class: 'btn-bg btn-bg-rect',
    });
    this.canvas.appendChild(this.btnBg);
  }

  buildBtnFill() {
    const fillRect = this.createRect({
      ...this.getRectColors('fill'),
      class: 'btn-fill btn-bg-rect',
    });

    const maskBase = this.createRect({ fill: '#000' });
    const maskWipe = this.createRect({
      fill: '#fff',
      class: 'btn-fill-mask js-btn-fill',
    });
    maskWipe.setAttribute('data-button-fill', '');

    const mask = createSvgEl('mask');
    mask.append(maskBase, maskWipe);

    const maskId = `btn-fill-mask-${Math.random().toString(36).slice(2, 9)}`;
    mask.setAttribute('id', maskId);
    fillRect.setAttribute('mask', `url(#${maskId})`);

    const defs = createSvgEl('defs');
    defs.appendChild(mask);
    this.canvas.appendChild(defs);
    this.canvas.appendChild(fillRect);

    const bgMaskBase = maskBase.cloneNode(true);
    bgMaskBase.setAttribute('fill', '#fff');

    const bgMaskWipe = maskWipe.cloneNode(true);
    bgMaskWipe.setAttribute('fill', '#000');
    bgMaskWipe.setAttribute('data-button-fill', '');

    const bgMask = createSvgEl('mask');
    const bgMaskId = `btn-bg-mask-${Math.random().toString(36).slice(2, 9)}`;
    bgMask.setAttribute('id', bgMaskId);
    bgMask.append(bgMaskBase, bgMaskWipe);
    defs.appendChild(bgMask);
    this.btnBg.setAttribute('mask', `url(#${bgMaskId})`);

    this.dom.el.querySelectorAll('[data-button-fill]').forEach((node) => setFillTransform(node, 120));
  }

  prepareContentLayout() {
    const { btnInner, btnContent, btnContentCloned } = this.dom;
    if (!btnInner) return;

    btnInner.style.overflow = 'hidden';
    btnInner.style.position = 'relative';

    [btnContent, btnContentCloned].forEach((layer) => {
      if (!layer) return;

      layer.style.height = '100%';
      layer.style.margin = '0';
      layer.style.transition = 'none';
      layer.style.willChange = 'transform';
      layer.style.backfaceVisibility = 'hidden';
    });
  }

  setInitialStates() {
    const { btnContentCloned } = this.dom;
    if (btnContentCloned) setTransform(btnContentCloned, 'translate3d(0, -120%, 0)');

    const clonedIcon = btnContentCloned?.querySelector('[data-button-icon-cloned]');
    if (clonedIcon) {
      clonedIcon.style.transition = 'none';
      setTransform(clonedIcon, 'translate3d(-250%, 0, 0)');
    }

    if (this.dom.btnIcon) {
      this.dom.btnIcon.style.transition = 'none';
    }
  }

  buildAnimateContentTl() {
    const { btnContent, btnContentCloned, btnIcon, el } = this.dom;
    if (!btnContent || !btnContentCloned) return;

    const clonedIcon = btnContentCloned.querySelector('[data-button-icon-cloned]');
    const fillMasks = [...el.querySelectorAll('[data-button-fill]')];
    const targets = [
      {
        el: btnContent,
        fromVars: { transform: 'translate3d(0, 0, 0)' },
        toVars: { transform: 'translate3d(0, 120%, 0)' },
        duration: DURATION_TEXT_OUT,
        ease: EASE_SMOOTH_IN,
      },
      {
        el: btnContentCloned,
        fromVars: { transform: 'translate3d(0, -120%, 0)' },
        toVars: { transform: 'translate3d(0, 0, 0)' },
        duration: DURATION_TEXT_IN,
        ease: EASE_SMOOTH_OUT,
      },
    ];

    if (btnIcon && clonedIcon) {
      targets.push(
        {
          el: btnIcon,
          fromVars: { transform: 'translate3d(0, 0, 0)' },
          toVars: { transform: 'translate3d(250%, 0, 0)' },
          duration: DURATION_TEXT_OUT,
          ease: EASE_SMOOTH_IN,
        },
        {
          el: clonedIcon,
          fromVars: { transform: 'translate3d(-250%, 0, 0)' },
          toVars: { transform: 'translate3d(0, 0, 0)' },
          duration: DURATION_TEXT_IN,
          ease: EASE_SMOOTH_OUT,
        },
      );
    }

    fillMasks.forEach((mask) => {
      targets.push({
        el: mask,
        fromVars: { transform: 'translate3d(0, 120%, 0)' },
        toVars: { transform: 'translate3d(0, 0, 0)' },
        duration: DURATION_FILL,
        ease: EASE_SMOOTH,
      });
      mask.style.transformBox = 'fill-box';
      mask.style.transformOrigin = 'center top';
    });

    this.contentTimeline = createTimeline(targets);
  }

  mouseEvents(e) {
    if (e.type === 'mouseenter') {
      this.contentTimeline?.play();
    } else if (e.type === 'mouseleave') {
      this.contentTimeline?.reverse();
    }
  }

  destroy() {
    this.canvas?.remove();
    this.dom.el.removeEventListener('mouseenter', this._onMouseEnter);
    this.dom.el.removeEventListener('mouseleave', this._onMouseLeave);
    this.contentTimeline?.kill();
  }
}

const AnimatedButtonMixin = (Base) =>
  class extends Base {
    connectedCallback() {
      if (this._button) return;
      requestAnimationFrame(() => {
        if (!this.isConnected || this._button) return;
        this._button = new AnimatedButton(this);
      });
    }

    disconnectedCallback() {
      this._button?.destroy();
      this._button = null;
    }
  };

class AnimatedAnchorElement extends AnimatedButtonMixin(HTMLAnchorElement) {}
class AnimatedSubmitButtonElement extends AnimatedButtonMixin(HTMLButtonElement) {}

if (!customElements.get('animated-button')) {
  customElements.define('animated-button', AnimatedAnchorElement, { extends: 'a' });
}

if (!customElements.get('animated-submit-button')) {
  customElements.define('animated-submit-button', AnimatedSubmitButtonElement, { extends: 'button' });
}

export { AnimatedButton };
